// client/src/main.tsx — composition root, 04 §9.1 (NORMATIVE order).
//
// §9.1's `configureApiClient` slot is `connectHttp` — the same place in the order. AUTH-004
// put the hand-written wrapper behind it while the OpenAPI contract did not exist (DEVIATIONS
// D-180, D-304); PROP-004 added the generated client of §9.3.2 beside it (`api/configure.ts`),
// configured from the same call with the same hooks. I18N-001 took the i18n import, and UI-003
// the toast host below.

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
// Side-effect FIRST: i18next initialises (§9.6) before any component renders, and this
// line MUST stay ABOVE './index.css' — that is the §9.1 order, not a preference.
import './i18n';
import './index.css'; // §3.2 — UI-002 replaces the file, not this import
import { routeTree } from './routeTree.gen';
import { isProblemStatus } from '@/api/problem-details';
import { connectHttp } from '@/session'; // §9.3.2 — bearer, X-Correlation-Id, 401 → refresh → replay, on both transports
import { Toasts } from '@/components/ui/Toasts';        // §5.9 — the toast region
import { SelectMenu } from '@/components/forms/SelectMenu'; // D-1839, D-3330 — every select's list

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000, // list/detail data; dashboards override per-query
      retry: (count, err) => count < 2 && !isProblemStatus(err, 400, 401, 403, 404, 422),
      refetchOnWindowFocus: true, // powers the 60s/on-focus notification poll (02 §12.5)
    },
    mutations: { retry: 0 }, // mutations NEVER auto-retry; Idempotency-Key covers user retries (§9.4)
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient }, // available to loaders/beforeLoad
  defaultPreload: 'intent',
  scrollRestoration: true,
});

// §9.3.2 / §5.11: a 401 the refresh could not cure clears the session and the cache, then
// lands on /login carrying the route the user was on, so a successful login restores it.
connectHttp(queryClient, () => {
  void router.navigate({ to: '/login', search: { redirect: router.state.location.href } });
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('#root is missing from index.html');

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      {/* Outside the router on purpose: a toast raised by the navigation that unmounted the
          page which raised it must still be delivered (§5.9). */}
      <Toasts />
      {/* The application's own list for every native select (D-1839). Mounted here rather than
          in a shell, so the tenant portal, the sign-in screens and the link flows get it too
          (D-3330). It fetches nothing. */}
      <SelectMenu />
    </QueryClientProvider>
  </StrictMode>,
);
// NO ThemeProvider in V1 (Appendix A §A.4 adds it). NO I18nextProvider needed —
// react-i18next binds to the singleton initialized by the './i18n' import.
